import "bootstrap/dist/css/bootstrap.min.css";
import { get, onValue, push, ref, update, remove } from "firebase/database";
import { useEffect, useState } from "react";
import MaskedDisplay from "../components/MaskedDisplay";
import { db } from "../Firebase";
import { pushRequest } from "../firebaseHelpers";
import { validatePhoneMessage } from "../utils/fieldValidation";

const AddMember = () => {
  // All hooks at the top
  const [regId, setRegId] = useState("");
  const [basic, setBasic] = useState(null);
  const [meta, setMeta] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadedId, setLoadedId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [registrationData, setRegistrationData] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestPhone, setRequestPhone] = useState("");
  const [requestName, setRequestName] = useState("");
  const [requestText, setRequestText] = useState("");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [urbanAreas, setUrbanAreas] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [workAreas, setWorkAreas] = useState([]);
  const [showMemberView, setShowMemberView] = useState(false);
  const [memberViewData, setMemberViewData] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const emptyForm = {
    name: "",
    fatherName: "",
    gender: "",
    relation: "",
    dob: "",
    aadhaar: "",
    maritalStatus: "",
    mobile: "",
    whatsapp: "",
    email: "",
    bloodGroup: "",
    currentAddress: "",
    birthAddress: "",
    educationStatus: "",
    educationState: "",
    educationDistrict: "",
    educationClass: "",
    educationSubject: "",
    workType: "",
    workState: "",
    workDistrict: "",
    workArea: "",
    workAreaName: "",
    workDepartment: "",
    workPosition: "",
  };

  const [form, setForm] = useState(emptyForm);
  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!requestName.trim()) {
      alert("कृपया नाम दर्ज करें");
      return;
    }
    if (!requestPhone.match(/^\d{10}$/)) {
      alert("कृपया 10 अंकों का फ़ोन नंबर दर्ज करें");
      return;
    }
    if (!requestText.trim()) {
      alert("कृपया अनुरोध विवरण भरें");
      return;
    }
    try {
      await pushRequest({
        type: "data_change",
        regId: loadedId,
        phone: requestPhone,
        name: requestName,
        message: requestText,
      });
      alert("आपका अनुरोध भेज दिया गया है।");
      setShowRequestModal(false);
      setRequestPhone("");
      setRequestName("");
      setRequestText("");
    } catch (err) {
      alert("अनुरोध भेजने में त्रुटि: " + err.message);
    }
  };

  useEffect(() => {
    if (loadedId) {
      const regRef = ref(db, `registration/${loadedId}`);
      onValue(regRef, (snapshot) => {
        if (snapshot.exists()) {
          setRegistrationData(snapshot.val());
        }
      });
    }

    // Fetch master data
    const fetchData = (path, setter) =>
      onValue(ref(db, path), (snap) =>
        setter(
          snap.exists()
            ? Object.entries(snap.val()).map(([id, v]) => ({ id, ...v }))
            : [],
        ),
      );

    fetchData("masters/states", setStates);
    fetchData("masters/cities", setCities);
    fetchData("masters/urbanAreas", setUrbanAreas);
    fetchData("masters/workTypes", setWorkTypes);
    fetchData("masters/workAreas", setWorkAreas);
  }, [loadedId]);

  const generate7DigitId = () => {
    return Math.floor(1000000 + Math.random() * 9000000).toString();
  };

  const computeAge = (dob) => {
    if (!dob) return "-";
    try {
      const d = new Date(dob);
      if (isNaN(d.getTime())) return "-";
      const diff = Date.now() - d.getTime();
      const age = Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
      return age;
    } catch (e) {
      return "-";
    }
  };

  // If masters are empty, optionally populate them from an external API
  useEffect(() => {
    const apiBase = process.env.REACT_APP_LOCATION_API || "";
    if (!apiBase) return;
    if (states.length > 0) return; // already have data

    (async () => {
      try {
        // fetch states
        const sRes = await fetch(`${apiBase}/states`);
        if (sRes.ok) {
          const sList = await sRes.json();
          if (Array.isArray(sList)) {
            for (const st of sList) {
              await push(ref(db, "masters/states"), { name: st.name || st });
            }
          }
        }

        // fetch cities
        const cRes = await fetch(`${apiBase}/cities`);
        if (cRes.ok) {
          const cList = await cRes.json();
          if (Array.isArray(cList)) {
            for (const ct of cList) {
              await push(ref(db, "masters/cities"), {
                state: ct.state,
                name: ct.name,
              });
            }
          }
        }

        // fetch urban areas
        const uRes = await fetch(`${apiBase}/urbanAreas`);
        if (uRes.ok) {
          const uList = await uRes.json();
          if (Array.isArray(uList)) {
            for (const ua of uList) {
              await push(ref(db, "masters/urbanAreas"), {
                state: ua.state,
                city: ua.city,
                name: ua.name,
              });
            }
          }
        }
      } catch (err) {
        console.error("Failed to populate masters from API", err);
      }
    })();
  }, [states]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "workType") {
      setForm((p) => ({ ...p, [name]: value, workArea: "" }));
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((p) =>
      name === "workType"
        ? { ...p, [name]: value, workArea: "" }
        : { ...p, [name]: value },
    );
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    if (!editForm.name || !editForm.relation) {
      alert("नाम और संबंध आवश्यक है");
      return;
    }
    try {
      await update(
        ref(db, `registration/${loadedId}/members/${editingMember}`),
        {
          ...editForm,
          state: registrationData?.basic?.state || editForm.state,
          city: registrationData?.basic?.city || editForm.city,
          updatedAt: Date.now(),
        },
      );
      alert("सदस्य अपडेट हो गया ✅");
      setShowModal(false);
      setEditingMember(null);
      setEditForm({});
    } catch {
      alert("सदस्य अपडेट करने में त्रुटि हुई ❌");
    }
  };

  const handleDeleteMember = async (memberKey) => {
    if (!window.confirm("क्या आप वाकई इस सदस्य को हटाना चाहते हैं?")) return;
    try {
      await remove(ref(db, `registration/${loadedId}/members/${memberKey}`));
      alert("सदस्य हटा दिया गया ✅");
    } catch {
      alert("सदस्य हटाने में त्रुटि हुई ❌");
    }
  };

  const v = (field) => (editingMember ? editForm[field] : form[field]);
  const ch = editingMember ? handleEditChange : handleChange;

  const relationOptions = [
    "मुखिया स्वयं",
    "पत्नी",
    "पुत्र",
    "पुत्री",
    "बहू",
    "सगी बहन",
    "पारिवारिक बहन",
    "सगा भाई",
    "पारिवारिक भाई",
    "दादा",
    "दादी",
    "परदादा",
    "परदादी",
    "चाचा",
    "चाची",
    "बड़े पापा",
    "बड़ी मम्मी",
    "बुआ",
    "फूपा",
    "नाना",
    "नानी",
    "मामा",
    "मामी",
    "मोसी",
    "मोसिया",
    "माँ",
    "पिता",
    "सास",
    "ससुर",
    "नाती",
    "नातिन",
    "पोता",
    "पोती",
    "भतीजा",
    "भतीजी",
    "भांजा",
    "भांजी",
    "देवरानी",
    "जिठानी",
    "देवर",
    "जेठ",
    "सरुभाई",
    "जीजा",
    "भाभी",
    "साला",
    "साली",
    "साराज",
    "दामाद",
    "व्यावहारिक रिश्ता",
    "अन्य",
  ];

  const renderMemberForm = () => (
    <div className="row g-3">
      <div className="col-12">
        <h6 className="text-primary fw-bold mb-0">👤 व्यक्तिगत जानकारी</h6>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">सदस्य का नाम *</label>
        <input
          className="form-control"
          name="name"
          value={v("name") || ""}
          onChange={ch}
          required
        />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">पिता / पति का नाम</label>
        <input
          className="form-control"
          name="fatherName"
          value={v("fatherName") || ""}
          onChange={ch}
        />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">लिंग *</label>
        <select
          className="form-select"
          name="gender"
          value={v("gender") || ""}
          onChange={ch}
          required
        >
          <option value="">चयन करें</option>
          <option>पुरुष</option>
          <option>महिला</option>
          <option>अन्य</option>
        </select>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">मुखिया से संबंध *</label>
        <select
          className="form-select"
          name="relation"
          value={v("relation") || ""}
          onChange={ch}
          required
        >
          <option value="">चयन करें</option>
          {relationOptions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">जन्मदिनांक *</label>
        <input
          type="date"
          className="form-control"
          name="dob"
          value={v("dob") || ""}
          onChange={ch}
          required
        />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">विवाहिक स्थिति</label>
        <select
          className="form-select"
          name="maritalStatus"
          value={v("maritalStatus") || ""}
          onChange={ch}
        >
          <option value="">चयन करें</option>
          <option>अविवाहित</option>
          <option>विवाहित</option>
          <option>विधवा/विधुर</option>
          <option>ब्रम्हचर्य आजीवन</option>
        </select>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">AADHAR No</label>
        <input
          className="form-control"
          name="aadhaar"
          value={v("aadhaar") || ""}
          onChange={ch}
          maxLength="12"
        />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">ब्लड ग्रुप</label>
        <select
          className="form-select"
          name="bloodGroup"
          value={v("bloodGroup") || ""}
          onChange={ch}
        >
          <option value="">चयन करें</option>
          <option>जानकारी नहीं</option>
          <option>A+</option>
          <option>A-</option>
          <option>B+</option>
          <option>B-</option>
          <option>AB+</option>
          <option>AB-</option>
          <option>O+</option>
          <option>O-</option>
        </select>
      </div>
      <div className="col-12 mt-2">
        <h6 className="text-success fw-bold mb-0">📞 संपर्क जानकारी</h6>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">मोबाइल *</label>
        <input
          type="tel"
          className="form-control"
          name="mobile"
          value={v("mobile") || ""}
          onChange={ch}
          maxLength="10"
          required
        />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">WhatsApp No</label>
        <input
          type="tel"
          className="form-control"
          name="whatsapp"
          value={v("whatsapp") || ""}
          onChange={ch}
          maxLength="10"
        />
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">Email ID</label>
        <input
          type="email"
          className="form-control"
          name="email"
          value={v("email") || ""}
          onChange={ch}
        />
      </div>
      <div className="col-12 mt-2">
        <h6 className="text-warning fw-bold mb-0">🏠 पता जानकारी</h6>
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">वर्त्तमान पता</label>
        <input
          className="form-control"
          name="currentAddress"
          value={v("currentAddress") || ""}
          onChange={ch}
        />
      </div>
      <div className="col-12">
        <label className="form-label fw-semibold">जन्म स्थान का पता</label>
        <input
          className="form-control"
          name="birthAddress"
          value={v("birthAddress") || ""}
          onChange={ch}
        />
      </div>
      <div className="col-12 mt-2">
        <h6 className="text-primary fw-bold mb-0">📚 शिक्षा की जानकारी</h6>
      </div>
      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">शिक्षा की स्थिति</label>
        <select
          className="form-select"
          name="educationStatus"
          value={v("educationStatus") || ""}
          onChange={ch}
        >
          <option value="">चयन करें</option>
          <option value="Running">Running</option>
          <option value="Complete">Complete</option>
        </select>
      </div>
      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">राज्य</label>
        <select
          className="form-select"
          name="educationState"
          value={v("educationState") || ""}
          onChange={ch}
        >
          <option value="">चयन करें</option>
          {states.map((s) => (
            <option key={s.id} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">जिला</label>
        <select
          className="form-select"
          name="educationDistrict"
          value={v("educationDistrict") || ""}
          onChange={ch}
        >
          <option value="">चयन करें</option>
          {cities
            .filter((c) => c.state === v("educationState"))
            .map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
        </select>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">कक्षा</label>
        <input
          className="form-control"
          name="educationClass"
          value={v("educationClass") || ""}
          onChange={ch}
        />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">विषय</label>
        <input
          className="form-control"
          name="educationSubject"
          value={v("educationSubject") || ""}
          onChange={ch}
        />
      </div>
      <div className="col-12 mt-2">
        <h6 className="text-info fw-bold mb-0">💼 कार्य की जानकारी</h6>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">कार्य का प्रकार</label>
        <select
          className="form-select"
          name="workType"
          value={v("workType") || ""}
          onChange={ch}
        >
          <option value="">चयन करें</option>
          {workTypes.map((w) => (
            <option key={w.id} value={w.name}>
              {w.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">राज्य</label>
        <select
          className="form-select"
          name="workState"
          value={v("workState") || ""}
          onChange={ch}
        >
          <option value="">चयन करें</option>
          {states.map((s) => (
            <option key={s.id} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">जिला</label>
        <select
          className="form-select"
          name="workDistrict"
          value={v("workDistrict") || ""}
          onChange={ch}
        >
          <option value="">चयन करें</option>
          {cities
            .filter((c) => c.state === v("workState"))
            .map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
        </select>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">कार्य क्षेत्र</label>
        <select
          className="form-select"
          name="workArea"
          value={v("workArea") || ""}
          onChange={ch}
          disabled={!v("workType")}
        >
          <option value="">चयन करें</option>
          {workAreas
            .filter((w) => w.workType === v("workType"))
            .map((w) => (
              <option key={w.id} value={w.name}>
                {w.name}
              </option>
            ))}
        </select>
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">विभाग/प्रकार</label>
        <input
          className="form-control"
          name="workDepartment"
          value={v("workDepartment") || ""}
          onChange={ch}
        />
      </div>
      <div className="col-12 col-md-6">
        <label className="form-label fw-semibold">पद/नाम</label>
        <input
          className="form-control"
          name="workPosition"
          value={v("workPosition") || ""}
          onChange={ch}
        />
      </div>
    </div>
  );
  const handleSearch = async (e) => {
    e.preventDefault();

    if (!regId) {
      alert("परिवार ID / Aadhaar / Phone दर्ज करें");
      return;
    }

    if (!passwordInput) {
      alert("कृपया पासवर्ड दर्ज करें");
      return;
    }

    // try direct registration id first (only if it's a valid Firebase path segment)
    const isValidPath = !/[.#$[\]]/.test(regId);
    const snap = isValidPath
      ? await get(ref(db, `registration/${regId}`))
      : null;

    if (snap && snap.exists()) {
      const val = snap.val();
      const storedPwd = val.meta?.password;
      if (!storedPwd) {
        alert(
          "इस परिवार के लिए पासवर्ड सेट नहीं है। कृपया एडमिन से संपर्क करें।",
        );
        return;
      }
      if (storedPwd !== passwordInput) {
        alert("पासवर्ड मेल नहीं खा रहा");
        return;
      }

      setBasic(val.basic || {});
      setMeta(val.meta || {});
      setLoadedId(regId);

      onValue(ref(db, `registration/${regId.trim()}/members`), (s) => {
        if (s.exists()) {
          const arr = Object.entries(s.val()).map(([id, v]) => ({ id, ...v }));
          setMembers(arr);
        } else setMembers([]);
      });

      return;
    }

    // fallback: search by phone / aadhaar / memberId across all registrations
    const allSnap = await get(ref(db, "registration"));
    if (!allSnap.exists()) {
      alert("❌ कोई रिकॉर्ड नहीं मिला");
      return;
    }

    const regs = allSnap.val();
    let found = null;
    for (const [fid, rv] of Object.entries(regs)) {
      // check family phone
      if (
        rv.basic &&
        (rv.basic.mobile === regId || rv.basic.displayName === regId)
      ) {
        found = { fid, rv };
        break;
      }

      // check members
      if (rv.members) {
        for (const [, mv] of Object.entries(rv.members)) {
          if (
            (mv.aadhaar && String(mv.aadhaar) === String(regId)) ||
            (mv.memberId && String(mv.memberId) === String(regId))
          ) {
            found = { fid, rv };
            break;
          }
        }
        if (found) break;
      }
    }

    if (!found) {
      alert("❌ कोई रिकॉर्ड नहीं मिला");
      return;
    }

    const storedPwd = found.rv.meta?.password;
    if (!storedPwd) {
      alert("पासवर्ड सेट नहीं है, एडमिन से संपर्क करें");
      return;
    }
    if (storedPwd !== passwordInput) {
      alert("पासवर्ड मेल नहीं खा रहा");
      return;
    }

    setBasic(found.rv.basic || {});
    setMeta(found.rv.meta || {});
    setLoadedId(found.fid);
    const membersArr = found.rv.members
      ? Object.entries(found.rv.members).map(([id, v]) => ({ id, ...v }))
      : [];
    setMembers(membersArr);
  };

  /* ================= ADD MEMBER ================= */
  const handleAddMember = async (e) => {
    e.preventDefault();

    if (!loadedId) {
      alert("कृपया पहले परिवार का डेटा लोड करें");
      return;
    }

    // Prevent adding when family is approved
    if (meta && meta.status === "approved") {
      setShowRequestModal(true);
      return;
    }

    const { name, mobile, relation, dob, gender } = form;
    if (!name || !relation) {
      alert("नाम और संबंध आवश्यक है");
      return;
    }
    if (!gender) {
      alert("लिंग चुनना आवश्यक है");
      return;
    }
    if (!dob) {
      alert("जन्मदिनांक आवश्यक है");
      return;
    }
    if (!mobile) {
      alert("मोबाइल नंबर आवश्यक है");
      return;
    }

    const phoneErr = validatePhoneMessage(mobile);
    if (phoneErr) {
      alert(phoneErr);
      return;
    }

    if (form.aadhaar) {
      const clean = String(form.aadhaar).replace(/\D/g, "");
      if (clean.length !== 12) {
        alert("आधार नंबर 12 अंकों का होना चाहिए");
        return;
      }
      const isDuplicateAadhaar = members.some(
        (m) => m.aadhaar && String(m.aadhaar).replace(/\D/g, "") === clean,
      );
      if (isDuplicateAadhaar) {
        alert("यह आधार नंबर पहले से इस परिवार में दर्ज है।");
        return;
      }
    }

    const memberId = generate7DigitId();

    // Include autofilled state and city in the saved data
    const memberData = {
      memberId,
      ...form,
      state: registrationData?.basic?.state || form.state,
      city: registrationData?.basic?.city || form.city,
      createdAt: Date.now(),
    };

    await push(ref(db, `registration/${loadedId}/members`), memberData);

    alert(`सदस्य जोड़ा गया ✅\nMember ID: ${memberId}`);

    setForm({
      name: "",
      fatherName: "",
      gender: "",
      relation: "",
      dob: "",
      aadhaar: "",
      maritalStatus: "",
      mobile: "",
      whatsapp: "",
      email: "",
      Mandirtype: "",
      bloodGroup: "",
      currentAddress: "",
      birthAddress: "",
      educationStatus: "",
      educationState: "",
      educationDistrict: "",
      educationClass: "",
      educationSubject: "",
      workType: "",
      workState: "",
      workDistrict: "",
      workArea: "",
      workAreaName: "",
      workDepartment: "",
      workPosition: "",
    });

    setShowModal(false);
  };

  return (
    <div className="container-fluid py-3 py-md-5 px-2 px-md-3">
      <div className="card shadow">
        <div className="card-header bg-success text-white fw-bold text-center py-3">
          ➕ सदस्य जोड़ें (Unique Family ID)
          {loadedId && (
            <div className="mt-2">
              <span className="badge bg-light text-dark fs-6">
                परिवार ID: {loadedId}
              </span>
            </div>
          )}
        </div>

        <div className="card-body p-3 p-md-4">
          {/* SEARCH */}
          <form
            onSubmit={handleSearch}
            className="row g-2 g-md-3 justify-content-center"
          >
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">परिवार ID</label>
              <input
                className="form-control form-control-sm form-control-md"
                value={regId}
                onChange={(e) => setRegId(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">पासवर्ड</label>
              <input
                type="password"
                className="form-control form-control-sm form-control-md"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
              />
            </div>

            <div className="col-12 text-center">
              <button className="btn btn-primary px-4 px-md-5">
                डेटा लोड करें
              </button>
            </div>
          </form>

          {/* Family Info Display */}
          {basic && (
            <div className="mt-4">
              <div className="row g-2 g-md-3 mb-3">
                <div className="col-6 col-md-4">
                  <div className="card p-2 p-md-3 shadow-sm h-100">
                    <div className="text-muted small fw-semibold">🏞️ राज्य</div>
                    <div className="fw-semibold fs-6 mt-1">
                      {basic.state || "-"}
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="card p-2 p-md-3 shadow-sm h-100">
                    <div className="text-muted small fw-semibold">🏙️ शहर</div>
                    <div className="fw-semibold fs-6 mt-1">
                      {basic.city || "-"}
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="card p-2 p-md-3 shadow-sm h-100">
                    <div className="text-muted small fw-semibold">
                      🌆 क्षेत्र प्रकार
                    </div>
                    <div className="fw-semibold fs-6 mt-1">
                      {basic.areaType || "-"}
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="card p-2 p-md-3 shadow-sm h-100">
                    <div className="text-muted small fw-semibold">
                      👨 मुखिया
                    </div>
                    <div className="fw-semibold fs-6 mt-1">
                      {basic.headName || "-"}
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="card p-2 p-md-3 shadow-sm h-100">
                    <div className="text-muted small fw-semibold">
                      📞 मोबाइल
                    </div>
                    <div className="fw-semibold fs-6 mt-1">
                      <MaskedDisplay
                        type="phone"
                        value={
                          (meta && (meta.contactNumber || meta.phone)) ||
                          basic.mobile ||
                          "-"
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="card p-2 p-md-3 shadow-sm h-100">
                    <div className="text-muted small fw-semibold">
                      👥 कुल सदस्य
                    </div>
                    <div className="fw-semibold fs-6 mt-1">
                      {basic.Totalmember || members.length || "-"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Badge */}
              <div className="text-center">
                {meta && meta.status === "approved" && (
                  <span className="badge bg-success p-2">✅ Approved</span>
                )}
                {meta && meta.status === "hold" && (
                  <span className="badge bg-warning text-dark p-2">
                    ⏸️ On Hold
                  </span>
                )}
                {meta && meta.status === "request_changes" && (
                  <span className="badge bg-danger p-2">
                    ❌ Request Changes
                  </span>
                )}
              </div>
            </div>
          )}

          {basic && (
            <>
              <hr className="my-4" />
              <h5 className="text-success mb-3">👨👩👧👦 सदस्य सूची</h5>

              {/* Mobile Card View */}
              <div className="d-md-none">
                {members.length === 0 ? (
                  <div className="alert alert-info text-center">
                    कोई सदस्य नहीं
                  </div>
                ) : (
                  members.map((m, i) => (
                    <div
                      key={m.id || m.memberId || i}
                      className="card mb-2 shadow-sm"
                    >
                      <div className="card-body p-2">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <div className="fw-bold">{m.name || "-"}</div>
                            <div className="small text-muted">
                              ID: {m.memberId || "-"}
                            </div>
                          </div>
                          <span className="badge bg-secondary">
                            {computeAge(m.dob)} वर्ष
                          </span>
                        </div>
                        <div className="small mb-2">
                          <div>
                            <strong>संबंध:</strong> {m.relation || "-"}
                          </div>
                          <div>
                            <strong>पिता:</strong> {m.fatherName || "-"}
                          </div>
                          <div>
                            <strong>लिंग:</strong> {m.gender || "-"}
                          </div>
                        </div>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-primary flex-fill"
                            onClick={() => {
                              setMemberViewData(m);
                              setShowMemberView(true);
                            }}
                          >
                            👁️ देखें
                          </button>
                          <button
                            className="btn btn-sm btn-warning flex-fill"
                            onClick={() => {
                              setEditingMember(m.id);
                              setEditForm({ ...m });
                              setShowModal(true);
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-sm btn-danger flex-fill"
                            onClick={() => handleDeleteMember(m.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block table-responsive">
                <table className="table table-hover table-bordered align-middle text-center">
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th className="text-start">सदस्य ID</th>
                      <th>नाम</th>
                      <th>पिता का नाम</th>
                      <th>लिंग</th>
                      <th>उम्र</th>
                      <th>संबंध</th>
                      <th>विकल्प</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-muted">
                          कोई सदस्य नहीं
                        </td>
                      </tr>
                    ) : (
                      members.map((m, i) => (
                        <tr key={m.id || m.memberId || i}>
                          <td>{i + 1}</td>
                          <td className="text-start">{m.memberId || "-"}</td>
                          <td>{m.name || "-"}</td>
                          <td>{m.fatherName || "-"}</td>
                          <td>{m.gender || "-"}</td>
                          <td>{computeAge(m.dob)}</td>
                          <td>{m.relation || "-"}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-1 justify-content-center">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  setMemberViewData(m);
                                  setShowMemberView(true);
                                }}
                              >
                                👁️
                              </button>
                              <button
                                className="btn btn-sm btn-outline-warning"
                                onClick={() => {
                                  setEditingMember(m.id);
                                  setEditForm({ ...m });
                                  setShowModal(true);
                                }}
                              >
                                ✏️
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteMember(m.id)}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* ADD MEMBER BUTTON */}
              <div className="text-center my-3">
                {meta && meta.status === "approved" ? (
                  <>
                    <div className="alert alert-info mb-3">
                      ℹ️ परिवार Approved है। बदलाव के लिए Admin से संपर्क करें।
                    </div>
                    <button
                      className="btn btn-warning btn-lg"
                      onClick={() => setShowRequestModal(true)}
                    >
                      डेटा परिवर्तन अनुरोध
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={() => { setEditingMember(null); setEditForm({}); setForm(emptyForm); setShowModal(true); }}
                  >
                    ➕ नया सदस्य जोड़ें
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Request Data Change Modal */}
      {showRequestModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.3)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">डेटा परिवर्तन अनुरोध</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRequestModal(false)}
                ></button>
              </div>
              <form onSubmit={handleRequestSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">
                      नाम <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={requestName}
                      onChange={(e) => setRequestName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      फोन नंबर <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      className="form-control"
                      value={requestPhone}
                      onChange={(e) => setRequestPhone(e.target.value)}
                      required
                      pattern="[0-9]{10}"
                      maxLength={10}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      विवरण / अनुरोध <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      value={requestText}
                      onChange={(e) => setRequestText(e.target.value)}
                      required
                      rows={3}
                      style={{ resize: "vertical", minHeight: "80px" }}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowRequestModal(false)}
                  >
                    रद्द करें
                  </button>
                  <button type="submit" className="btn btn-primary">
                    अनुरोध भेजें
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Member Modal */}
      {showModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  {editingMember
                    ? "✏️ सदस्य संपादित करें"
                    : "➕ नया सदस्य जोड़ें"}
                </h5>
                <button
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowModal(false);
                    setEditingMember(null);
                    setEditForm({});
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={
                    editingMember ? handleUpdateMember : handleAddMember
                  }
                >
                  {/* Personal Information Section */}
                  <div className="mb-4">
                    <h6 className="text-primary fw-bold mb-3 p-2 bg-light rounded">
                      👤 व्यक्तिगत जानकारी
                    </h6>
                    <div className="row g-2 g-md-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          सदस्य का नाम *
                        </label>
                        <input
                          className="form-control form-control-sm"
                          name="name"
                          value={v("name") || ""}
                          onChange={ch}
                          required
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          पिता / पति का नाम
                        </label>
                        <input
                          className="form-control form-control-sm"
                          name="fatherName"
                          value={v("fatherName") || ""}
                          onChange={ch}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">लिंग *</label>
                        <select
                          className="form-select form-select-sm"
                          name="gender"
                          value={v("gender") || ""}
                          onChange={ch}
                          required
                        >
                          <option value="">चयन करें</option>
                          <option>पुरुष</option>
                          <option>महिला</option>
                          <option>अन्य</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          मुखिया से संबंध *
                        </label>
                        <select
                          className="form-select form-select-sm"
                          name="relation"
                          value={v("relation") || ""}
                          onChange={ch}
                          required
                        >
                          <option value="">चयन करें</option>
                          {[
                            "मुखिया स्वयं",
                            "पत्नी",
                            "पुत्र",
                            "पुत्री",
                            "बहू",
                            "सगी बहन",
                            "पारिवारिक बहन",
                            "सगा भाई",
                            "पारिवारिक भाई",
                            "दादा",
                            "दादी",
                            "परदादा",
                            "परदादी",
                            "चाचा",
                            "चाची",
                            "बड़े पापा",
                            "बड़ी मम्मी",
                            "बुआ",
                            "फूपा",
                            "नाना",
                            "नानी",
                            "मामा",
                            "मामी",
                            "मोसी",
                            "मोसिया",
                            "माँ",
                            "पिता",
                            "सास",
                            "ससुर",
                            "नाती",
                            "नातिन",
                            "पोता",
                            "पोती",
                            "भतीजा",
                            "भतीजी",
                            "भांजा",
                            "भांजी",
                            "देवरानी",
                            "जिठानी",
                            "देवर",
                            "जेठ",
                            "सरुभाई",
                            "जीजा",
                            "भाभी",
                            "साला",
                            "साली",
                            "साराज",
                            "दामाद",
                            "व्यावहारिक रिश्ता",
                            "अन्य",
                          ].map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          जन्मदिनांक *
                        </label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          name="dob"
                          value={v("dob") || ""}
                          onChange={ch}
                          required
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          विवाहिक स्थिति
                        </label>
                        <select
                          className="form-select form-select-sm"
                          name="maritalStatus"
                          value={v("maritalStatus") || ""}
                          onChange={ch}
                        >
                          <option value="">चयन करें</option>
                          <option>अविवाहित</option>
                          <option>विवाहित</option>
                          <option>विधवा/विधुर</option>
                          <option>ब्रम्हचर्य आजीवन</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          AADHAR No
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          name="aadhaar"
                          value={v("aadhaar") || ""}
                          onChange={ch}
                          placeholder="आधार नंबर दर्ज करें"
                          maxLength="12"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          ब्लड ग्रुप
                        </label>
                        <select
                          className="form-select form-select-sm"
                          name="bloodGroup"
                          value={v("bloodGroup") || ""}
                          onChange={ch}
                        >
                          <option value="">चयन करें</option>
                          <option>जानकारी नहीं</option>
                          <option>A+</option>
                          <option>A-</option>
                          <option>B+</option>
                          <option>B-</option>
                          <option>AB+</option>
                          <option>AB-</option>
                          <option>O+</option>
                          <option>O-</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information Section */}
                  <div className="mb-4">
                    <h6 className="text-success fw-bold mb-3 p-2 bg-light rounded">
                      📞 संपर्क जानकारी
                    </h6>
                    <div className="row g-2 g-md-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          मोबाइल *
                        </label>
                        <input
                          type="tel"
                          className="form-control form-control-sm"
                          name="mobile"
                          value={v("mobile") || ""}
                          onChange={ch}
                          placeholder="मोबाइल नंबर दर्ज करें"
                          maxLength="10"
                          required
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          WhatsApp No
                        </label>
                        <input
                          type="tel"
                          className="form-control form-control-sm"
                          name="whatsapp"
                          value={v("whatsapp") || ""}
                          onChange={ch}
                          placeholder="व्हाट्सएप नंबर दर्ज करें"
                          maxLength="10"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          Email ID
                        </label>
                        <input
                          type="email"
                          className="form-control form-control-sm"
                          name="email"
                          value={v("email") || ""}
                          onChange={ch}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address Information Section */}
                  <div className="mb-4">
                    <h6 className="text-warning fw-bold mb-3 p-2 bg-light rounded">
                      🏠 पता जानकारी
                    </h6>
                    <div className="row g-2 g-md-3">
                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          वर्त्तमान पता
                        </label>
                        <input
                          className="form-control form-control-sm"
                          name="currentAddress"
                          value={v("currentAddress") || ""}
                          onChange={ch}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          जन्म स्थान का पता
                        </label>
                        <input
                          className="form-control form-control-sm"
                          name="birthAddress"
                          value={v("birthAddress") || ""}
                          onChange={ch}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Education Information Section */}
                  <div className="mb-4">
                    <h6 className="text-primary fw-bold mb-3 p-2 bg-light rounded">
                      📚 शिक्षा की जानकारी
                    </h6>
                    <div className="row g-2 g-md-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          शिक्षा की स्थिति
                        </label>
                        <select
                          className="form-select form-select-sm"
                          name="educationStatus"
                          value={v("educationStatus") || ""}
                          onChange={ch}
                        >
                          <option value="">चयन करें</option>
                          <option value="Running">Running</option>
                          <option value="Complete">Complete</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">राज्य</label>
                        <select
                          className="form-select form-select-sm"
                          name="educationState"
                          value={v("educationState") || ""}
                          onChange={ch}
                        >
                          <option value="">चयन करें</option>
                          {states.map((s) => (
                            <option key={s.id} value={s.name}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">जिला</label>
                        <select
                          className="form-select form-select-sm"
                          name="educationDistrict"
                          value={v("educationDistrict") || ""}
                          onChange={ch}
                        >
                          <option value="">चयन करें</option>
                          {cities
                            .filter((c) => c.state === v("educationState"))
                            .map((c) => (
                              <option key={c.id} value={c.name}>
                                {c.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">कक्षा</label>
                        <input
                          className="form-control form-control-sm"
                          name="educationClass"
                          value={v("educationClass") || ""}
                          onChange={ch}
                          placeholder="कक्षा"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">विषय</label>
                        <input
                          className="form-control form-control-sm"
                          name="educationSubject"
                          value={v("educationSubject") || ""}
                          onChange={ch}
                          placeholder="विषय"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Work Information Section */}
                  <div className="mb-4">
                    <h6 className="text-info fw-bold mb-3 p-2 bg-light rounded">
                      💼 कार्य की जानकारी
                    </h6>
                    <div className="row g-2 g-md-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          कार्य का प्रकार
                        </label>
                        <select
                          className="form-select form-select-sm"
                          name="workType"
                          value={v("workType") || ""}
                          onChange={ch}
                        >
                          <option value="">चयन करें</option>
                          {workTypes.map((w) => (
                            <option key={w.id} value={w.name}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">राज्य</label>
                        <select
                          className="form-select form-select-sm"
                          name="workState"
                          value={v("workState") || ""}
                          onChange={ch}
                        >
                          <option value="">चयन करें</option>
                          {states.map((s) => (
                            <option key={s.id} value={s.name}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">जिला</label>
                        <select
                          className="form-select form-select-sm"
                          name="workDistrict"
                          value={v("workDistrict") || ""}
                          onChange={ch}
                        >
                          <option value="">चयन करें</option>
                          {cities
                            .filter((c) => c.state === v("workState"))
                            .map((c) => (
                              <option key={c.id} value={c.name}>
                                {c.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          कार्य क्षेत्र
                        </label>
                        <select
                          className="form-select form-select-sm"
                          name="workArea"
                          value={v("workArea") || ""}
                          onChange={ch}
                          disabled={!v("workType")}
                        >
                          <option value="">चयन करें</option>
                          {workAreas
                            .filter((w) => w.workType === v("workType"))
                            .map((w) => (
                              <option key={w.id} value={w.name}>
                                {w.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">
                          विभाग/प्रकार
                        </label>
                        <input
                          className="form-control form-control-sm"
                          name="workDepartment"
                          value={v("workDepartment") || ""}
                          onChange={ch}
                          placeholder="विभाग/प्रकार"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">पद/नाम</label>
                        <input
                          className="form-control form-control-sm"
                          name="workPosition"
                          value={v("workPosition") || ""}
                          onChange={ch}
                          placeholder="पद/नाम"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="text-center mt-4 mb-2">
                    <button className="btn btn-success btn-lg px-4 w-100 w-md-auto">
                      {editingMember ? "✏️ अपडेट करें" : "➕ सदस्य जोड़ें"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Member view modal - COMPLETE WITH ALL SECTIONS */}
      {showMemberView && memberViewData && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto" }}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ minWidth: "100%", maxWidth: "none" }}
          >
            <div className="modal-content">
              <div className="modal-header bg-info text-white sticky-top">
                <h5 className="modal-title">
                  👤 सदस्य विवरण - {memberViewData.name}
                </h5>
                <button
                  className="btn-close btn-close-white"
                  onClick={() => setShowMemberView(false)}
                ></button>
              </div>
              <div className="modal-body p-3 p-md-4">
                {/* Personal Information Section */}
                <div className="mb-4">
                  <h6 className="text-primary fw-bold mb-3 p-2 bg-light rounded">
                    👤 व्यक्तिगत जानकारी
                  </h6>
                  <div className="row g-2 g-md-3">
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          सदस्य ID
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.memberId || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          सदस्य का नाम
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.name || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          पिता / पति का नाम
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.fatherName || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">लिंग</div>
                        <div className="fw-semibold">
                          {memberViewData.gender || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          मुखिया से संबंध
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.relation || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          जन्मदिनांक
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.dob || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          विवाहिक स्थिति
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.maritalStatus || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          ब्लड ग्रुप
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.bloodGroup || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          AADHAR No
                        </div>
                        <div className="fw-semibold">
                          <MaskedDisplay
                            type="aadhaar"
                            value={memberViewData.aadhaar}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="mb-4">
                  <h6 className="text-success fw-bold mb-3 p-2 bg-light rounded">
                    📞 संपर्क जानकारी
                  </h6>
                  <div className="row g-2 g-md-3">
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          मोबाइल
                        </div>
                        <div className="fw-semibold">
                          <MaskedDisplay
                            type="phone"
                            value={memberViewData.mobile}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          WhatsApp No
                        </div>
                        <div className="fw-semibold">
                          <MaskedDisplay
                            type="phone"
                            value={memberViewData.whatsapp}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          Email ID
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.email || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Information Section */}
                <div className="mb-4">
                  <h6 className="text-warning fw-bold mb-3 p-2 bg-light rounded">
                    🏠 पता जानकारी
                  </h6>
                  <div className="row g-2 g-md-3">
                    <div className="col-12">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          वर्त्तमान पता
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.currentAddress || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          जन्म स्थान का पता
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.birthAddress || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Education Information Section */}
                <div className="mb-4">
                  <h6 className="text-primary fw-bold mb-3 p-2 bg-light rounded">
                    📚 शिक्षा की जानकारी
                  </h6>
                  <div className="row g-2 g-md-3">
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          शिक्षा की स्थिति
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.educationStatus || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          राज्य
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.educationState || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">जिला</div>
                        <div className="fw-semibold">
                          {memberViewData.educationDistrict || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          कक्षा
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.educationClass || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">विषय</div>
                        <div className="fw-semibold">
                          {memberViewData.educationSubject || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Work Information Section */}
                <div className="mb-4">
                  <h6 className="text-info fw-bold mb-3 p-2 bg-light rounded">
                    💼 कार्य की जानकारी
                  </h6>
                  <div className="row g-2 g-md-3">
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          कार्य का प्रकार
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.workType || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          राज्य
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.workState || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">जिला</div>
                        <div className="fw-semibold">
                          {memberViewData.workDistrict || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          कार्य क्षेत्र
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.workArea || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          विभाग/प्रकार
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.workDepartment || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="card p-2 p-md-3">
                        <div className="text-muted small fw-semibold">
                          पद/नाम
                        </div>
                        <div className="fw-semibold">
                          {memberViewData.workPosition || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer sticky-bottom bg-light">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowMemberView(false)}
                >
                  बंद करें
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddMember;
